package com.crowpay.views.components.project

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.isUnread
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.views.components.contactInfo
import com.crowpay.views.components.nav.windowIsNarrow
import com.crowpay.views.components.space
import com.crowpay.views.components.unreadIconWhen
import com.crowpay.views.dialogs.GenericConfirmationDialog

import com.crowpay.views.screens.client.ClientProject
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.contractor.ContractorProject
import com.crowpay.views.screens.contractor.ContractorProjects
import com.crowpay.views.screens.contractor.CreateProject
import com.crowpay.views.screens.contractor.EditProject
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.HasId
import com.lightningkite.now
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer


private object RecentsTracker {
    @Serializable
    data class Usage(
        val id: UUID,
        val timestamp: Instant = now()
    )

    const val MAX_RECENTS = 3

    val recents = PersistentProperty("recent-projects", emptyList(), ListSerializer(Usage.serializer()))

    fun used(id: UUID) {
        val list = recents.value.toMutableList()
        val idx = list.indexOfFirst { it.id == id }

        if (idx == -1) list.add(Usage(id))
        else list[idx] = Usage(id)

        recents.value = list.sortedByDescending { it.timestamp }.take(MAX_RECENTS)
    }

    fun <T : HasId<UUID>> ReactiveContext.getRecents(items: List<T>): List<T> = recents().mapNotNull { (id, _) -> items.find { it._id == id } }

    fun clear() { recents.value = emptyList() }
}

fun ViewWriter.projectList(
    lens: ProjectLens,
    projects: Readable<List<Project>>,
    contacts: Readable<List<Contact>>,
    showArchivedProjects: Property<Boolean>,
): ViewModifiable {
    return col {
        spacing = 0.dp
        compact - rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
            expanding - title("Projects")
            row {
                existsDefaultFalse {
                    val active = contractorStatus()
                    active != ContractorStatus.AwaitingApproval
                }
                centered - subTitle("Show Archived Projects")
                centered - checkbox {
                    checked bind showArchivedProjects
                }
            }
        }

        space(AppDimensions.majorColumnSpacing)

        atStart - primaryButton - button {
            existsDefaultFalse { selectedContractor() != null && contractorStatus() == ContractorStatus.Approved }
            specCenteredText("+ Project")
            onClick {
                screenNavigator.navigate(CreateProject())
            }
        }

        space(AppDimensions.majorColumnSpacing)

        subTitle {
            existsDefaultFalse {
                val active = contractorStatus()
                projects().isEmpty() && active != ContractorStatus.AwaitingApproval
            }
            content = when (lens) {
                ProjectLens.Contractor -> "You have no contracts yet. Create a new Contract."
                ProjectLens.Customer -> "You have no Projects"
                ProjectLens.Preview -> ""
            }
        }

        scrolls - expanding - col {
            col {
                val recents = shared { RecentsTracker.run { getRecents(projects()) } }
                compact - col {
                    row {
                        expanding - boldSubTitle("Recents")
                        centered - textButton - button {
                            spacing = 5.dp
                            smallBody("Clear Recents")
                            onClick { RecentsTracker.clear() }
                        }
                    }
                    greySeparator()
                }
                existsDefaultFalse { recents().isNotEmpty() }
                col {
                    forEachUpdating(recents) {
                        renderProjectCard(contacts, lens, it)
                    }
                }
                space(2.0)
            }

            col {
                forEachUpdating(projects) {
                    renderProjectCard(contacts, lens, it)
                }
            }
        }
    }
}

private fun ViewWriter.renderProjectCard(contacts: Readable<List<Contact>>, lens: ProjectLens, project: Readable<Project>): ViewModifiable {
    val contact = shared {
        val p = project()
        contacts().find {
            when (it) {
                is Client -> {
                    it._id == p.client
                }

                is Contractor -> {
                    it._id == p.contractor
                }

                else -> false
            }
        }
    }
    val unread = shared {
        project().state != ProjectState.Creating &&
                project().isUnread(notNullSession().userId)
    }

    return outlined - row {
        spacing = 0.dp
        expanding - button {
            row {
                space(0.1)
                expanding - rowCollapsingToColumn(60.rem) {
                    spacing = AppDimensions.sectionIndent
                    rowCollapsingToColumn(AppDimensions.wideCollapseWidth) {
                        row {
                            spacing = AppDimensions.expandButtonSpace
                            centered - unreadIconWhen { unread() }
                            centered - subTitle {
                                ::content { project().name }
                            }
                            expanding - centered - onlyWhen(false) { windowIsNarrow() } - body {
                                align = Align.End
                                ::content { project().state.displayName }
                                dynamicTheme {
                                    ProjectStateSemantic(project().state)
                                }
                            }
                        }

                        greySeparator()

                        row {
                            ::visible{ contact() != null }
                            sizeConstraints(width = 3.rem, height = 3.rem) - centered - image {
                                ::exists{ contact()?.image != null }
                                ::source{ ImageRemote(contact()?.image?.location ?: "") }
                            }

                            centered - contactInfo(contact)
                        }
                    }

                    expanding - gravity(Align.End, Align.Center) - onlyWhen { !windowIsNarrow() } - body {
                        align = Align.End
                        ::content{ project().state.displayName }
                        dynamicTheme {
                            ProjectStateSemantic(project().state)
                        }
                    }
                }
                space(0.1)
            }
            onClick {
                val p = project.await()
                RecentsTracker.used(p._id)
                val to = when {
                    lens == ProjectLens.Customer -> ClientProject(p._id)
                    p.state == ProjectState.Creating -> EditProject(p._id)
                    else -> ContractorProject(p._id)
                }
                screenNavigator.navigate(to)
            }
        }
        deleteButton - padded - button {
            spacing = AppDimensions.sectionIndent
            existsDefaultFalse { project().state == ProjectState.Creating }
            centered - icon(Icon.trash, "Delete")
            onClick {
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Delete Draft?",
                        message = "You are about to delete this project draft. Deleted drafts cannot be recovered. Delete this draft?",
                        confirmationText = "Delete Draft",
                        confirmationTheme = DangerButtonSemantic,
                        onSubmit = { confirmed ->
                            if (confirmed) {
                                notNullSession().projects[project()._id].delete()
                                screenNavigator.reset(ContractorProjects())

                            }
                        }
                    )
                )
            }
        }
    }
}
